import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import Layout from '../../component/layout';
import { toastify } from '../../../../utils/toast';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../app/z-store/store';
import { addApprovalWorkflowRequestsUrl, deleteApprovalWorkflowRequestsUrl, updateApprovalWorkflowRequestURL, getApprovalWorkflowRequestsUrl, getUsersURL } from "../../../../utils/urls";

const ApprovalWorkflow = () => {
  const { approvalType } = useParams();
  const location = useLocation();

  const moduleType = location.state?.moduleType || approvalType;
  const moduleName =
    location.state?.moduleName ||
    (approvalType
      ? `${approvalType.charAt(0).toUpperCase() + approvalType.slice(1).replace(/-/g, ' ')}`
      : 'Approval Workflow');

  const facilityId = useFacilityStore((state) => state.facilityId);

  const [steps, setSteps] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [workflowId, setWorkflowId] = useState(null);
  const [workflowExists, setWorkflowExists] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showApproverDialog, setShowApproverDialog] = useState(false);
  const [currentStepNumber, setCurrentStepNumber] = useState(null);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [showStepNameDialog, setShowStepNameDialog] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [editingStepNumber, setEditingStepNumber] = useState(null);

  useEffect(() => {
    if (!moduleType || !facilityId) return;
    fetchWorkflowData();
  }, [moduleType, facilityId]);



  const fetchWorkflowData = async () => {
    setIsLoading(true);
    try {
      const customersRes = await makeRequest2(`${getUsersURL}/${facilityId}`, 'GET');
      if (customersRes.success) {
        setCustomers(customersRes.data?.customers || customersRes.data || []);
      }

      const response = await makeRequest2(
        `${getApprovalWorkflowRequestsUrl}/${facilityId}?module=${moduleType}`,
        'GET'
      );
      const workflowsData = response.data?.data || response.data;

      if (response.success && workflowsData?.length) {
        const workflow = workflowsData[0];
        setWorkflowId(workflow._id);
        setWorkflowExists(true);
        const formattedSteps = workflow.steps.map((step) => ({
          stepNumber: step.stepNumber,
          name: step.name,
          approvers: Array.isArray(step.approvers)
            ? step.approvers.map((approver) => ({
              _id: approver._id,
              name: approver.fullName,
            }))
            : [],
        }));
        setSteps(formattedSteps);
      } else {
        setWorkflowExists(false);
        setSteps([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toastify('Error fetching data', 'error');
      setSteps([]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleAddStep = () => {
    const stepNumber = steps.length ? Math.max(...steps.map((s) => s.stepNumber)) + 1 : 1;
    setSteps((prev) => [...prev, { stepNumber, name: `New Step ${stepNumber}`, approvers: [] }]);
  };

  const handleEditStepName = (stepNumber) => {
    const step = steps.find((s) => s.stepNumber === stepNumber);
    setNewStepName(step?.name || '');
    setEditingStepNumber(stepNumber);
    setShowStepNameDialog(true);
  };

  const saveStepName = () => {
    if (!newStepName.trim()) {
      toastify('Step name cannot be empty', 'error');
      return;
    }
    setSteps((prev) =>
      prev.map((step) =>
        step.stepNumber === editingStepNumber ? { ...step, name: newStepName } : step
      )
    );
    setShowStepNameDialog(false);
  };

  const handleRemoveStep = (stepNumber) => {
    confirmDialog({
      message: 'Are you sure you want to remove this step?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setSteps((prev) =>
          prev
            .filter((s) => s.stepNumber !== stepNumber)
            .map((s, idx) => ({ ...s, stepNumber: idx + 1 }))
        );
      },
    });
  };

  const openApproverDialog = (stepNumber) => {
    setCurrentStepNumber(stepNumber);
    const currentStep = steps.find((s) => s.stepNumber === stepNumber);
    setSelectedApprovers(
      customers.filter((c) => currentStep?.approvers.some((a) => a._id === c._id))
    );
    setShowApproverDialog(true);
  };

  const handleApproverSelection = () => {
    setSteps((prev) =>
      prev.map((step) =>
        step.stepNumber === currentStepNumber
          ? {
            ...step,
            approvers: selectedApprovers.map((c) => ({
              _id: c._id,
              name: c.fullName,
            })),
          }
          : step
      )
    );
    setShowApproverDialog(false);
  };

  const handleRemoveApprover = (stepNumber, approverId) => {
    confirmDialog({
      message: 'Are you sure you want to remove this approver?',
      header: 'Remove Approver',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setSteps((prev) =>
          prev.map((step) =>
            step.stepNumber === stepNumber
              ? { ...step, approvers: step.approvers.filter((a) => a._id !== approverId) }
              : step
          )
        );
      },
    });
  };

  const handleSaveChanges = async () => {
    if (!steps.length) {
      toastify('Add at least one approval step', 'error');
      return;
    }
    if (steps.some((s) => !s.approvers.length)) {
      toastify('Each step needs at least one approver', 'error');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      module: moduleType,
      facilityId,
      steps: steps.map((step) => ({
        stepNumber: step.stepNumber,
        name: step.name,
        approvers: step.approvers.map((a) => a._id),
      })),
    };
    try {
      const url = workflowExists
        ? `${updateApprovalWorkflowRequestURL}/${facilityId}/${workflowId}`
        : `${addApprovalWorkflowRequestsUrl}/${facilityId}`;
      const method = workflowExists ? 'PUT' : 'POST';
      const response = await makeRequest2(url, method, payload);
      if (!response.success) {
        toastify(response.error || 'Error saving workflow', 'error');
        return;
      }
      if (!workflowExists && response.data?._id) {
        setWorkflowId(response.data._id);
        setWorkflowExists(true);
      }
      toastify(response.message || 'Workflow saved successfully', 'success');

      await fetchWorkflowData();

    } catch (err) {
      console.error('Error saving workflow:', err);
      toastify(err.message || 'Unknown error', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClear = () => {
    confirmDialog({
      message: 'Are you sure you want to clear all steps?',
      header: 'Clear Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => setSteps([]),
    });
  };

  const handleDeleteWorkflow = () => {
    if (!workflowId) return;
    confirmDialog({
      message: 'Are you sure you want to delete this workflow? This action cannot be undone.',
      header: 'Delete Workflow',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setIsSubmitting(true);
        try {
          const response = await makeRequest2(
            `${deleteApprovalWorkflowRequestsUrl}/${facilityId}/${workflowId}`,
            'DELETE'
          );
          if (response.success) {
            toastify('Workflow deleted', 'success');
            setSteps([]);
            setWorkflowId(null);
            setWorkflowExists(false);
          } else {
            toastify(response.error || 'Error deleting workflow', 'error');
          }
        } catch {
          toastify('Error deleting workflow', 'error');
        } finally {
          setIsSubmitting(false);
        }
      },
    });
  };

  if (!facilityId) {
    return (
      <Layout>
        <div className="text-center p-5">Please select a facility first.</div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading workflow data...</p>
        </div>
      </Layout>
    );
  }

  const approverDialogFooter = (
    <>
      <Button label="Cancel" className="btn btn-danger" onClick={() => setShowApproverDialog(false)} />
      <Button label="Add Approver" className="btn btn-primary" onClick={handleApproverSelection} />
    </>
  );

  const stepNameDialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowStepNameDialog(false)} />
      <Button label="Save" icon="pi pi-check" onClick={saveStepName} />
    </>
  );

  return (
    <Layout>
      <ConfirmDialog />

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/facility/procurement_management/settings">Approvals</Link></li>
                <li className="breadcrumb-item">{moduleName} Approval Workflow</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/settings">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5>{moduleName} &ndash; Approval Workflow for Facility</h5>
            </div>
            <div className="card-body">
              <div className="mb-4 d-flex justify-content-between">
                <Button label="+ Add Step" className="btn btn-success" onClick={handleAddStep} disabled={isSubmitting} />
                {workflowExists && <Button label="Delete Workflow" className="btn btn-outline-danger" onClick={handleDeleteWorkflow} disabled={isSubmitting} />}
              </div>

              <div className="row">
                {steps.sort((a, b) => a.stepNumber - b.stepNumber).map((step) => (
                  <div key={step.stepNumber} className="col-md-6 col-lg-4 mb-3">
                    <div className="card border">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>

                            <span className="d-inline-flex justify-content-center align-items-center" style={{ width: '28px', height: '28px', backgroundColor: '#0d6efd', borderRadius: '50%', color: '#fff', marginRight: '10px', fontSize: '14px' }}>
                              {step.stepNumber}
                            </span>
                            <strong>{step.name}</strong>
                          </div>
                          <div>
                            <button className="btn btn-sm btn-link text-primary" onClick={() => handleEditStepName(step.stepNumber)} disabled={isSubmitting} title="Edit Step Name"><i className="ti ti-edit"></i></button>
                            <button className="btn btn-sm btn-link text-danger" onClick={() => handleRemoveStep(step.stepNumber)} disabled={isSubmitting} title="Remove Step"><i className="ti ti-trash"></i></button>
                          </div>
                        </div>

                        <div className="mb-3">
                          <h6 className="mb-2">Approvers</h6>
                          {step.approvers.length ? (
                            <div className="list-group">
                              {step.approvers.map((approver) => (
                                <div key={approver._id} className="list-group-item d-flex justify-content-between align-items-center">
                                  <div>{approver.name}</div>
                                  <button className="btn btn-sm btn-link text-danger" onClick={() => handleRemoveApprover(step.stepNumber, approver._id)} disabled={isSubmitting} title="Remove Approver"><i className="ti ti-trash"></i></button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted">No approvers added</div>
                          )}
                          <Button label="+ Select Approvers" className="btn btn-link p-0 mt-2" onClick={() => openApproverDialog(step.stepNumber)} disabled={isSubmitting} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!steps.length && (
                  <div className="col-12">
                    <div className="alert alert-info">
                      No approval steps defined yet. Click "Add Step" to begin creating the workflow.
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 d-flex justify-content-end gap-2">
                <Button label="Clear All" className="btn btn-danger" onClick={handleClear} disabled={isSubmitting || !steps.length} />
                <Button
                  label={workflowExists ? "Update Workflow" : "Create Workflow"}
                  className="btn btn-primary"
                  onClick={handleSaveChanges}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog header="Select Approvers" visible={showApproverDialog} style={{ width: '70vw' }} footer={approverDialogFooter} onHide={() => setShowApproverDialog(false)}>
        <DataTable
          value={customers}
          selection={selectedApprovers}
          onSelectionChange={(e) => setSelectedApprovers(e.value)}
          dataKey="_id"
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25]}
          emptyMessage="No customers found."
        >
          <Column selectionMode="multiple" style={{ width: '3em' }} />
          <Column field="fullName" header="Full Name" sortable />
          <Column field="email" header="Email" sortable />
          <Column field="phoneNumber" header="Phone" sortable />
        </DataTable>
      </Dialog>

      <Dialog header="Edit Step Name" visible={showStepNameDialog} style={{ width: '400px' }} footer={stepNameDialogFooter} onHide={() => setShowStepNameDialog(false)}>
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="stepName">Step Name</label>
            <InputText id="stepName" value={newStepName} onChange={(e) => setNewStepName(e.target.value)} autoFocus />
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default ApprovalWorkflow;
